import { type MouseEvent, useEffect, useRef, useState } from 'react'

import { type Rendering, type RendererFor, rendererFor as lookUp } from './renderers'

/**
 * A marked-up text, drawn.
 *
 * It asks the lookup which renderer draws this format and then draws whatever comes back — markup put
 * into the page, or a file shown as one. What it never does is decide from the *editor* what to draw: the
 * format travels with the content, so a note and a document written in the same markup are drawn by the
 * same renderer without either area knowing which one that is.
 *
 * A renderer that draws itself as somebody types redraws on every change; one that has to be asked for
 * stays where it was until it is. That difference is the renderer's to declare, and this is the only
 * place that reads it.
 */
export interface PreviewProps {
  readonly text: string
  /** Which markup this is, as a media type — the content's own, never the editor's. */
  readonly format: string
  /** Names the region for a screen reader. */
  readonly label: string
  /** Reported as the preview is scrolled, from 0 to 1, so the text can follow it. */
  readonly onScroll?: (fraction: number) => void
  /** Where to scroll to, from 0 to 1. `null` means "do not move me". */
  readonly scrollTo?: number | null
  /** For a test, or for the day a renderer comes from somewhere else. */
  readonly rendererFor?: RendererFor
  /**
   * Said once the drawn text is on the screen — including when nothing could draw it.
   *
   * Printing is what this exists for: a rendering is asked for asynchronously and Asciidoctor is
   * fetched the first time it is needed, so "print this" has to wait for something rather than hope.
   * Nothing else listens, and nothing here changes when nobody does.
   */
  readonly onDrawn?: () => void
}

/**
 * A link into the text itself, followed inside the preview.
 *
 * AsciiDoc's cross-references and footnotes are ordinary links to an `id` in the same document, and
 * MyLib's address lives in the hash — so letting the browser follow one would leave the library and
 * land nowhere. Caught here rather than rewritten by the renderer: what the link *means* is "go to
 * that place in this text", and this is the only place that knows where the text is on the screen.
 *
 * A link to anywhere else is untouched and opens as it always did.
 */
function jumpInsideTheText(event: MouseEvent<HTMLDivElement>): void {
  const link = (event.target as Element | null)?.closest?.('a')
  const href = link?.getAttribute('href')
  if (link === null || link === undefined || href === null || href === undefined) {
    return
  }
  if (!href.startsWith('#') || href.length < 2) {
    return
  }
  event.preventDefault()
  // Looked for inside *this* preview and not in the page: the same text can be drawn twice — beside
  // the editor and on a tile — and a search of the whole document would jump to whichever came first.
  const wanted = decodeURIComponent(href.slice(1))
  const inside = link.closest('.preview__body')
  const found = [...(inside?.querySelectorAll('[id]') ?? [])].find((one) => one.id === wanted)
  found?.scrollIntoView({ block: 'start' })
}

export function Preview({
  text,
  format,
  label,
  onScroll,
  scrollTo = null,
  rendererFor,
  onDrawn,
}: PreviewProps) {
  const lookingUp = rendererFor ?? lookUp
  const [drawn, setDrawn] = useState<Rendering | null>(null)
  const [nothingDraws, setNothingDraws] = useState(false)
  const region = useRef<HTMLDivElement>(null)

  /*
   * Kept in a ref rather than depended on: a caller that hands in a fresh function on every render
   * would otherwise redraw the text on every render, and drawing is the expensive thing here.
   */
  const report = useRef(onDrawn)
  useEffect(() => {
    report.current = onDrawn
  }, [onDrawn])

  useEffect(() => {
    let current = true
    void (async () => {
      const renderer = await lookingUp(format)
      if (!current) {
        return
      }
      if (renderer === null) {
        setNothingDraws(true)
        report.current?.()
        return
      }
      setNothingDraws(false)
      const rendering = await renderer.render(text)
      if (current) {
        setDrawn(rendering)
        report.current?.()
      }
    })()
    return () => {
      // The text changed again while this rendering was in flight. Dropping the older one is what keeps
      // a fast typist from seeing the preview flicker backwards.
      current = false
    }
  }, [text, format, lookingUp])

  useEffect(() => {
    const element = region.current
    if (element === null || scrollTo === null) {
      return
    }
    const room = element.scrollHeight - element.clientHeight
    if (room > 0) {
      element.scrollTop = scrollTo * room
    }
  }, [scrollTo, drawn])

  if (nothingDraws) {
    return (
      <div className="preview preview--none" aria-label={label} role="region">
        <p className="preview__note">
          MyLib cannot draw this kind of text yet. It is kept exactly as you write it.
        </p>
      </div>
    )
  }

  return (
    <div
      className="preview"
      role="region"
      aria-label={label}
      ref={region}
      onScroll={(event) => {
        const element = event.currentTarget
        const room = element.scrollHeight - element.clientHeight
        onScroll?.(room <= 0 ? 0 : element.scrollTop / room)
      }}
    >
      {drawn?.kind === 'file' && <iframe className="preview__file" src={drawn.url} title={label} />}
      {drawn?.kind === 'html' && (
        /*
          Put into the page as markup, which is what drawing markup means. It is safe because of where
          the string comes from and not because of what is done to it here: each renderer answers for
          its own output — Markdown by having raw HTML switched off at the parser, AsciiDoc by being
          sanitised, because AsciiDoc has no such switch. See `markdown.ts` and `asciidoc.ts`.
        */
        <div
          className="preview__body"
          onClick={jumpInsideTheText}
          dangerouslySetInnerHTML={{ __html: drawn.html }}
        />
      )}
    </div>
  )
}
